import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import type { Premio } from './types';
import ModalPremio from './ModalPremio';

interface Props {
  rifaId: number;
  premios: Premio[];
  aoMudarPremios: (lista: Premio[]) => void;
}

function pad4(n: number): string {
  return String(n).padStart(4, '0');
}

export default function PremiosBloco({ rifaId, premios, aoMudarPremios }: Props) {
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [sobre, setSobre] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<Premio | null>(null);

  const semFoto = premios.some((p) => !p.imagem_url);

  async function salvarOrdem(lista: Premio[]) {
    setSalvando(true);
    setErro(null);
    try {
      const N = lista.length;
      // Fase 1: ordens temporárias no alto (evita conflito da chave única).
      for (let i = 0; i < N; i++) {
        const { error: e } = await supabase.rpc('rifa_salvar_premio', {
          p_rifa: rifaId,
          p_descricao: lista[i].descricao,
          p_premio: lista[i].premio_id,
          p_ordem: 20 - i,
        });
        if (e) throw e;
      }
      // Fase 2: ordens finais 1..N.
      let novaLista: Premio[] = lista;
      for (let i = 0; i < N; i++) {
        const { data, error: e } = await supabase.rpc('rifa_salvar_premio', {
          p_rifa: rifaId,
          p_descricao: lista[i].descricao,
          p_premio: lista[i].premio_id,
          p_ordem: i + 1,
        });
        if (e) throw e;
        novaLista = (data as Premio[]) || novaLista;
      }
      aoMudarPremios(novaLista);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível reordenar.'));
    } finally {
      setSalvando(false);
    }
  }

  function mover(indice: number, delta: number) {
    if (salvando) return;
    const alvo = indice + delta;
    if (alvo < 0 || alvo >= premios.length) return;
    const lista = [...premios];
    const [item] = lista.splice(indice, 1);
    lista.splice(alvo, 0, item);
    aoMudarPremios(lista); // otimista
    salvarOrdem(lista);
  }

  function soltar(indiceAlvo: number) {
    if (arrastando == null || arrastando === indiceAlvo) {
      setArrastando(null);
      setSobre(null);
      return;
    }
    const lista = [...premios];
    const [item] = lista.splice(arrastando, 1);
    lista.splice(indiceAlvo, 0, item);
    setArrastando(null);
    setSobre(null);
    aoMudarPremios(lista);
    salvarOrdem(lista);
  }

  function abrirNovo() {
    setEditando(null);
    setModalAberto(true);
  }

  function abrirEditar(p: Premio) {
    setEditando(p);
    setModalAberto(true);
  }

  return (
    <section className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <div className="flex items-center justify-between gap-3 px-1">
        <h2 className="text-[17px] font-bold text-foreground-950">Prêmios</h2>
        <button
          type="button"
          onClick={abrirNovo}
          className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 text-[14px] font-semibold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-add-line" />
          </span>
          Adicionar prêmio
        </button>
      </div>

      {semFoto && (
        <div className="mt-3 flex items-start gap-3 rounded-2xl bg-warning-50 px-4 py-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-warning-600">
            <i className="ri-information-line" />
          </span>
          <p className="text-[14px] leading-relaxed text-foreground-800">
            A página de compra usa a foto do 1º prêmio. Coloque uma foto para a ação entre amigos ficar
            atraente.
          </p>
        </div>
      )}

      {premios.length === 0 ? (
        <p className="mt-3 px-1 text-[14px] text-foreground-500">
          Nenhum prêmio ainda. Toque em “Adicionar prêmio”.
        </p>
      ) : (
        <ul className="mt-2">
          {premios.map((p, i) => (
            <li
              key={p.premio_id}
              draggable={!salvando}
              onDragStart={() => {
                setArrastando(i);
                setSobre(i);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setSobre(i);
              }}
              onDragLeave={() => {
                if (sobre === i) setSobre(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                soltar(i);
              }}
              onDragEnd={() => {
                setArrastando(null);
                setSobre(null);
              }}
              className={`flex items-center gap-3 rounded-2xl px-1 py-2.5 transition-colors ${
                sobre === i && arrastando != null ? 'bg-primary-50/70' : ''
              }`}
            >
              {/* Foto */}
              <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-secondary-50">
                {p.imagem_url ? (
                  <img
                    src={p.imagem_url}
                    alt={p.descricao}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <span className="flex flex-col items-center justify-center gap-0.5">
                    <span className="flex h-6 w-6 items-center justify-center text-secondary-300">
                      <i className="ri-gift-line text-2xl" />
                    </span>
                    <span className="text-[12px] font-medium text-amber-600">sem foto</span>
                  </span>
                )}
              </div>

              {/* Texto */}
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-semibold text-foreground-950">
                  {p.ordem}º · {p.descricao}
                </p>
                {p.doado_por ? (
                  <p className="truncate text-[13px] text-foreground-500">
                    doado por {p.doado_por}
                  </p>
                ) : (
                  <p className="text-[13px] text-foreground-400">sem doador informado</p>
                )}
                {p.sorteado && (
                  <span className="mt-1 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-success-100 px-2 py-0.5 text-[12px] font-semibold text-success-800">
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-check-line" />
                    </span>
                    apurado nº {p.numero_sorteado != null ? pad4(p.numero_sorteado) : '—'}
                  </span>
                )}
              </div>

              {/* Ações */}
              <div className="flex shrink-0 flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={() => mover(i, -1)}
                  disabled={i === 0 || salvando}
                  aria-label="Mover para cima"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-foreground-400 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <i className="ri-arrow-up-s-line text-lg" />
                </button>
                <button
                  type="button"
                  onClick={() => mover(i, 1)}
                  disabled={i === premios.length - 1 || salvando}
                  aria-label="Mover para baixo"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-foreground-400 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <i className="ri-arrow-down-s-line text-lg" />
                </button>
              </div>

              <button
                type="button"
                onClick={() => abrirEditar(p)}
                aria-label={`Editar ${p.descricao}`}
                className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl text-foreground-400 transition-colors hover:bg-background-100"
              >
                <i className="ri-more-2-fill text-lg" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {salvando && (
        <p className="mt-2 flex items-center gap-2 px-1 text-sm text-secondary-500">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-loader-4-line animate-spin" />
          </span>
          Salvando a ordem…
        </p>
      )}
      {erro && (
        <p className="mt-2 px-1 text-sm text-danger-600" role="alert">
          {erro}
        </p>
      )}

      {modalAberto && (
        <ModalPremio
          rifaId={rifaId}
          premio={editando}
          proximaOrdem={premios.length + 1}
          aoFechar={() => setModalAberto(false)}
          aoSalvo={(lista) => {
            aoMudarPremios(lista);
            setModalAberto(false);
          }}
        />
      )}
    </section>
  );
}